import React, { Fragment } from 'react';
import dayjs from 'dayjs';
import cn from 'classnames';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import { debounce, isEqual, range, reverse } from 'lodash';
import { Popover, Transition } from '@headlessui/react';
import { getMonth, getYear } from 'date-fns';
import {
  AiOutlineDoubleLeft,
  AiOutlineDoubleRight,
  AiOutlineCalendar,
  AiOutlineCloseCircle,
} from 'react-icons/ai';
import FormSelectStatic from 'modules/transactions/components/FormSelectStatic';
import Button from '../Button';

const years = reverse(
  range(1990, getYear(new Date()) + 1, 1).map((x) => ({
    label: x,
    value: x,
  })),
);
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const monthOptions = months.map((x) => ({ label: x, value: x }));

function BadgeButton({ name, onClick, active, children }) {
  const handleSelect = (e) => {
    e.preventDefault();
    onClick(name);
  };
  return (
    <a
      className={cn(
        'flex-1 text-center px-2.5 py-1 rounded-md text-sm hover:bg-gray-200 transition',
        active
          ? 'bg-gray-900 text-white hover:bg-gray-900'
          : 'bg-gray-100 text-gray-800',
      )}
      href="/"
      onClick={handleSelect}
    >
      {children}
    </a>
  );
}

BadgeButton.propTypes = {
  name: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  active: PropTypes.bool.isRequired,
  children: PropTypes.string.isRequired,
};

const FORMAT_BY_MODE = {
  day: 'MM/DD/YYYY',
  month: 'MMM YYYY',
  year: 'YYYY',
};

const MAP_TYPE = {
  day: 'hour',
  month: 'day',
  year: 'month',
};

function Callback({ trigger, onChange }) {
  React.useEffect(() => {
    onChange(trigger);
    // eslint-disable-next-line
  }, [trigger]);
  return null;
}

function FormDatePicker({ onChange, defaultValue, panelClassName }) {
  const [isEmpty, setIsEmpty] = React.useState(true);
  const [dateFrom, setDateFrom] = React.useState(new Date(defaultValue));
  const [dateTo, setDateTo] = React.useState(new Date(defaultValue));

  const popOverRef = React.useRef();
  const isOpenedOnce = React.useRef(false);

  // eslint-disable-next-line
  const fetch = React.useCallback(
    debounce((d, m) => {
      const payload = {};
      payload.type = MAP_TYPE[m];
      payload.from = dayjs(d?.dateFrom).format('YYYY-MM-DD 00:00:00');
      payload.to = dayjs(d?.dateTo).format('YYYY-MM-DD 23:59:59');
      onChange((state) => {
        const newState = {
          ...state,
          ...payload,
        };
        return isEqual(state, newState) ? state : newState;
      });
    }, 300),
    [],
  );
  const handleCallback = (e) => {
    if (e || !isOpenedOnce.current) {
      isOpenedOnce.current = true;
      return;
    }
    setIsEmpty(false);
    fetch({ dateFrom, dateTo }, 'day');
  };
  const handleClear = () => {
    onChange({ to: '', from: '' });
    setDateFrom(new Date(defaultValue));
    setDateTo(new Date(defaultValue));
    setIsEmpty(true);
  };
  const label = React.useMemo(
    () =>
      `${dayjs(dateFrom).format(FORMAT_BY_MODE.day)} - ${dayjs(dateTo).format(
        FORMAT_BY_MODE.day,
      )}`,
    [dateTo, dateFrom],
  );

  const renderHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }) => (
    <div className="flex m-[10px] justify-center">
      <Button
        icon={AiOutlineDoubleLeft}
        className="rounded-r text-light-gray-400 border-gray-100 mr-1"
        size="xxs"
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
      />
      <FormSelectStatic
        name="years"
        key={getYear(date)}
        data={years}
        value={{
          label: getYear(date),
          value: getYear(date),
        }}
        callBack={(value) => {
          changeYear(value);
        }}
        withClear={false}
        containerClassName="!w-[250px]"
      />
      <FormSelectStatic
        name="months"
        key={months[getMonth(date)]}
        data={monthOptions}
        value={{
          label: months[getMonth(date)],
          value: months[getMonth(date)],
        }}
        callBack={(value) => changeMonth(months.indexOf(value))}
        withClear={false}
        containerClassName="!w-[300px]"
      />
      <Button
        icon={AiOutlineDoubleRight}
        className="rounded-r text-light-gray-400 border-gray-100 ml-1"
        size="xxs"
        onClick={increaseMonth}
        disabled={nextMonthButtonDisabled}
      />
    </div>
  );

  return (
    <Popover className="relative">
      {({ open }) => (
        <>
          <Callback trigger={open} onChange={handleCallback} />
          <div className="flex gap-1">
            <Popover.Button
              as={Button}
              ref={popOverRef}
              size="xs"
              className="font-semibold border-gray-100 rounded"
            >
              <div className="min-w-[160px] px-2 p-1 flex justify-between  items-center gap-x-2 text-light-gray-400">
                <div className="font-light">
                  {isEmpty ? 'Select Date' : label}
                </div>
                <div className="flex gap-2">
                  <AiOutlineCalendar />
                </div>
              </div>
            </Popover.Button>
            {!isEmpty && (
              <Button
                icon={AiOutlineCloseCircle}
                className="rounded-l text-light-gray-400 border-gray-100"
                size="xxs"
                onClick={handleClear}
              />
            )}
          </div>
          <Transition
            show={open}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            as={Fragment}
          >
            <Popover.Panel
              className={cn('absolute z-50 w-[600px]', panelClassName)}
            >
              <div className="p-1 pt-3 bg-white rounded-lg border shadow-primary-lg mt-2 border-gray-100">
                <div className="flex gap-2">
                  <DatePicker
                    maxDate={new Date()}
                    renderCustomHeader={renderHeader}
                    selected={dateFrom}
                    onChange={setDateFrom}
                    inline
                    disabledKeyboardNavigation
                  />
                  <DatePicker
                    minDate={new Date(dateFrom)}
                    maxDate={new Date()}
                    renderCustomHeader={renderHeader}
                    selected={dateTo}
                    onChange={setDateTo}
                    inline
                    disabledKeyboardNavigation
                  />
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}

FormDatePicker.defaultProps = {
  panelClassName: '',
};

FormDatePicker.propTypes = {
  panelClassName: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  defaultValue: PropTypes.string.isRequired,
};

Callback.propTypes = {
  onChange: PropTypes.func.isRequired,
  trigger: PropTypes.bool.isRequired,
};

export default FormDatePicker;
